// The Resourcing page's figures, shaped for @olytic/foundation/resourcing.
// Pure: the payload (resourcingApi.ts) and the view settings in, formatted
// cells, rows, totals and panels out, so it tests without a browser.
//
// The figures come from the stored roll-ups. With every status shown and
// pipeline hours as quoted, a person's month is their resource_period row and
// a role's its resource_role_period row, as stored. Hiding a status or
// weighting the pipeline subsets the per-project roll-up
// (resource_project_period) instead, since no stored row answers that.
import {
  type ResourceCell,
  type ResourceCellRef,
  type ResourceGroup,
  type ResourceHeat,
  type ResourceLegendItem,
  type ResourceLine,
  type ResourceMonth,
  type ResourceMove,
  type ResourceRow,
  utilizationHeat,
} from '@olytic/foundation/resourcing';

import type { MetricRowItem } from '@olytic/foundation';

import type { ObjectKey } from '../navigation';
import { STREAM_LABELS } from '../opportunities/revenueStreams';
import type {
  AssignmentStatus,
  PersonPeriod,
  ProjectPeriod,
  ResourceAssignment,
  ResourcePerson,
  ResourceRole,
  ResourceWork,
  ResourcingData,
  RolePeriod,
} from './resourcingApi';

// ── Months ──────────────────────────────────────────────────────────────────

const SHORT = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const LONG = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];

/** "2026-10" → months since year 0; NaN when malformed. */
export function monthIndex(key: string): number {
  const match = /^(\d{4})-(\d{2})$/.exec(key);
  return match ? Number(match[1]) * 12 + Number(match[2]) - 1 : NaN;
}
export const monthKey = (index: number): string => `${Math.floor(index / 12)}-${String((index % 12) + 1).padStart(2, '0')}`;
export const addMonths = (key: string, n: number): string => monthKey(monthIndex(key) + n);
export function monthRange(from: string, to: string): string[] {
  const out: string[] = [];
  for (let i = monthIndex(from); i <= monthIndex(to); i += 1) out.push(monthKey(i));
  return out;
}
/** "Oct 2026", or "October 2026" long. */
export function monthName(key: string, long = false): string {
  const i = monthIndex(key);
  if (Number.isNaN(i)) return key;
  return `${(long ? LONG : SHORT)[i % 12]} ${Math.floor(i / 12)}`;
}
const shortMonth = (key: string): string => SHORT[monthIndex(key) % 12] ?? key;
const yearOf = (key: string): string => key.slice(0, 4);

export type TimeframeKey = 'thisMonth' | 'lastMonth' | 'next3' | 'next6' | 'next12' | 'thisYear';
export interface Timeframe {
  key: TimeframeKey;
  label: string;
  from: string;
  to: string;
}

/** The time frames, relative to the current month. */
export function timeframes(current: string): Timeframe[] {
  const year = current.slice(0, 4);
  return [
    { key: 'thisMonth', label: 'This month', from: current, to: current },
    { key: 'lastMonth', label: 'Last month', from: addMonths(current, -1), to: addMonths(current, -1) },
    { key: 'next3', label: 'Next 3 months', from: current, to: addMonths(current, 2) },
    { key: 'next6', label: 'Next 6 months', from: current, to: addMonths(current, 5) },
    { key: 'next12', label: 'Next 12 months', from: current, to: addMonths(current, 11) },
    { key: 'thisYear', label: 'This year', from: `${year}-01`, to: `${year}-12` },
  ];
}
export const DEFAULT_TIMEFRAME: TimeframeKey = 'next6';

/** This month in UTC, YYYY-MM: what the page asks for before the server says. */
export function utcMonth(today = new Date()): string {
  return monthKey(today.getUTCFullYear() * 12 + today.getUTCMonth());
}

// ── Formatting ──────────────────────────────────────────────────────────────

export const fmt = (n: number): string => Math.round(n).toLocaleString('en-US');
export const hrs = (n: number): string => `${fmt(n)}h`;
const pct = (n: number): string => `${Math.round(n * 100)}%`;
const line = (label: string, value: string, over = false): ResourceLine => ({ label, value, tone: over ? 'over' : 'default' });
export const STATUS_LABEL: Record<AssignmentStatus, string> = { confirmed: 'Confirmed', assigned: 'Assigned', forecast: 'Forecast' };

// ── Settings ────────────────────────────────────────────────────────────────

export type View = 'people' | 'projects' | 'timeline';
export type PersonSort = 'name' | 'avg' | 'peak' | 'free';
export type ProjectSort = 'start' | 'name' | 'hours' | 'unstaffed';

export interface Settings {
  view: View;
  personGroup: 'role' | 'none';
  personSort: PersonSort;
  projectGroup: 'status' | 'none';
  projectSort: ProjectSort;
  show: Record<AssignmentStatus, boolean>;
  weighted: boolean;
  /** The month selected by its heading, or null for every month in view. */
  focus: string | null;
}

export const DEFAULT_SETTINGS: Settings = {
  view: 'people',
  personGroup: 'none',
  personSort: 'name',
  projectGroup: 'none',
  projectSort: 'start',
  show: { confirmed: true, assigned: true, forecast: true },
  weighted: false,
  focus: null,
};

/** Every status shown, as quoted: the stored person and role roll-ups answer as they are. */
const isPlain = (s: Pick<Settings, 'show' | 'weighted'>): boolean =>
  s.show.confirmed && s.show.assigned && s.show.forecast && !s.weighted;

// ── Keys ────────────────────────────────────────────────────────────────────

/** A row's work: its project when it has one, else its opportunity. */
export function workKeyOf(row: { project_id: string | null; opportunity_id: string | null }): string {
  return row.project_id ? `project:${row.project_id}` : `opportunity:${row.opportunity_id ?? ''}`;
}
export const workKey = (w: ResourceWork): string => `${w.kind}:${w.id}`;
/** Won work: a project, or a won deal. */
export const isDeal = (w: ResourceWork): boolean => w.kind === 'opportunity' && !w.won;
export const workHref = (w: ResourceWork): string => `/${w.kind === 'project' ? 'projects' : 'opportunities'}/${encodeURIComponent(w.id)}`;
export const workObject = (w: ResourceWork): ObjectKey => (w.kind === 'project' ? 'projects' : 'opportunities');
export const personHref = (id: string): string => `/people/${encodeURIComponent(id)}`;
export const NO_ROLE = 'none';

/** What kind of work it is: its revenue stream, else a project or a won deal. */
export function workType(w: ResourceWork): string {
  if (w.stream) return STREAM_LABELS[w.stream] ?? w.stream;
  return w.kind === 'project' ? 'Project' : w.won ? 'Won' : 'Deal';
}
/** A work row's second line: the deal's likelihood, or the work's type. */
export const workSub = (w: ResourceWork): string =>
  `${w.name} · ${isDeal(w) ? `${Math.round(w.probability)}% likely` : workType(w)}`;

// ── The model ───────────────────────────────────────────────────────────────

export interface PersonMonth {
  cap: number;
  note: string;
  booked: number;
  /** booked ÷ capacity; with no capacity, 9 when anything is booked. */
  u: number;
  billed: number;
}
export interface RoleMonth {
  cap: number;
  booked: number;
  unstaffed: number;
  /** Capacity less booked less unstaffed: negative is short. */
  free: number;
}
/**
 * A piece of work in a month. Demand is what it needs, assigned or not: its
 * sold hours by role, or everything assigned when that is more. Unstaffed is
 * demand nobody is named for, counted role by role so one role's extra hours
 * never cover another's gap; over is named hours beyond the sold ones.
 */
export interface WorkMonth {
  demand: number;
  resourced: number;
  unstaffed: number;
  over: number;
  people: number;
}

/** One role on one piece of work in one month, before the view's settings. */
interface RoleNeed {
  key: string;
  role: string;
  sold: number;
  named: number;
  unnamed: number;
}

/** Lookups over one payload, so each figure is a map read rather than a scan. */
export class Planner {
  readonly data: ResourcingData;
  readonly settings: Settings;
  readonly plain: boolean;
  readonly roles: ResourceRole[];
  private readonly roleById = new Map<string, ResourceRole>();
  readonly personById = new Map<string, ResourcePerson>();
  readonly workByKey = new Map<string, ResourceWork>();
  private readonly periods = new Map<string, PersonPeriod>();
  private readonly rolePeriods = new Map<string, RolePeriod>();
  private readonly ppByWork = new Map<string, ProjectPeriod[]>();
  private readonly ppByPerson = new Map<string, ProjectPeriod[]>();
  /** A won deal's work key → the project it became, which holds its hours. */
  private readonly alias = new Map<string, string>();
  private readonly needs = new Map<string, Map<string, RoleNeed>>();
  private readonly needsByRole = new Map<string, RoleNeed[]>();

  constructor(data: ResourcingData, settings: Settings) {
    this.data = data;
    this.settings = settings;
    this.plain = isPlain(settings);
    this.roles = [...data.roles].sort((a, b) => a.sort_order - b.sort_order || a.name.localeCompare(b.name));
    for (const r of this.roles) this.roleById.set(r.id, r);
    for (const p of data.people) this.personById.set(p.id, p);
    for (const w of data.work) this.workByKey.set(workKey(w), w);
    for (const w of data.work) {
      if (w.kind === 'project' && w.opportunity_id) this.alias.set(`opportunity:${w.opportunity_id}`, workKey(w));
    }
    for (const p of data.periods) this.periods.set(`${p.app_user_id}|${p.month}`, p);
    for (const r of data.role_periods) this.rolePeriods.set(`${r.resource_role_id}|${r.month}`, r);
    const push = (map: Map<string, ProjectPeriod[]>, key: string, row: ProjectPeriod) => {
      const list = map.get(key);
      if (list) list.push(row);
      else map.set(key, [row]);
    };
    const need = (key: string, role: string, month: string): RoleNeed => {
      const byRole = this.needs.get(`${key}|${month}`) ?? new Map<string, RoleNeed>();
      this.needs.set(`${key}|${month}`, byRole);
      let n = byRole.get(role);
      if (!n) {
        n = { key, role, sold: 0, named: 0, unnamed: 0 };
        byRole.set(role, n);
        const list = this.needsByRole.get(`${role}|${month}`);
        if (list) list.push(n);
        else this.needsByRole.set(`${role}|${month}`, [n]);
      }
      return n;
    };
    for (const pp of data.project_periods) {
      const key = this.keyOf(pp);
      push(this.ppByWork, `${key}|${pp.month}`, pp);
      if (pp.app_user_id) push(this.ppByPerson, `${pp.app_user_id}|${pp.month}`, pp);
      const n = need(key, pp.resource_role_id ?? NO_ROLE, pp.month);
      if (pp.app_user_id) n.named += pp.confirmed_hours + pp.assigned_hours;
      else n.unnamed += pp.confirmed_hours + pp.assigned_hours + pp.forecast_hours;
    }
    for (const d of data.demand) need(this.keyOf(d), d.resource_role_id, d.month).sold += d.hours;
  }

  /** A row's work, with a won deal's rows on the project it became. */
  keyOf(row: { project_id: string | null; opportunity_id: string | null }): string {
    const key = workKeyOf(row);
    return this.alias.get(key) ?? key;
  }
  /** A won deal shown as its project instead. */
  isAliased(w: ResourceWork): boolean {
    return this.alias.has(workKey(w));
  }

  /** The share of a deal's hours the view counts: its probability when weighted. */
  private weightOf(key: string): number {
    const work = this.workByKey.get(key);
    return this.settings.weighted && work && isDeal(work) ? work.probability / 100 : 1;
  }

  /** A role's need as shown: demand, the named hours the view counts, the gap and any excess. */
  private shownNeed(n: RoleNeed): { demand: number; gap: number; over: number } {
    const w = this.weightOf(n.key);
    const demand = Math.max(n.sold, n.named + n.unnamed);
    const gap = this.settings.show.forecast ? (demand - n.named) * w : 0;
    const over = n.sold > 0 ? Math.max(0, n.named - n.sold) * w : 0;
    return { demand: demand * w, gap, over };
  }

  role(id: string | null): ResourceRole | undefined {
    return id ? this.roleById.get(id) : undefined;
  }
  roleName(id: string | null): string {
    return this.role(id)?.name ?? 'No role';
  }

  /** A project period's hours as shown: hidden statuses are 0, open-deal hours weighted when asked. */
  shownPP(pp: ProjectPeriod): { confirmed: number; assigned: number; forecast: number } {
    const { show, weighted } = this.settings;
    const w = weighted ? pp.probability / 100 : 1;
    return {
      confirmed: show.confirmed ? pp.confirmed_hours : 0,
      assigned: show.assigned ? pp.assigned_hours * w : 0,
      forecast: show.forecast ? pp.forecast_hours * w : 0,
    };
  }

  /** One assignment's hours as shown. */
  shown(a: ResourceAssignment): number {
    if (!this.settings.show[a.status]) return 0;
    if (!this.settings.weighted || a.status === 'confirmed') return a.hours;
    const work = this.workByKey.get(this.keyOf(a));
    return a.hours * ((work?.probability ?? 100) / 100);
  }

  personMonth(id: string, month: string): PersonMonth {
    const period = this.periods.get(`${id}|${month}`);
    // No row: no capacity that month (capacity is generated a month at a time, 0038).
    const cap = period ? period.capacity_hours : 0;
    let booked: number;
    if (this.plain && period) booked = period.booked_hours;
    else {
      booked = 0;
      for (const pp of this.ppByPerson.get(`${id}|${month}`) ?? []) {
        const s = this.shownPP(pp);
        booked += s.confirmed + s.assigned;
      }
    }
    return { cap, note: period?.capacity_note ?? '', booked, u: cap ? booked / cap : booked ? 9 : 0, billed: period?.billed_hours ?? 0 };
  }

  /** Demand nobody is named for, for the role in the month: hours, and how many pieces of work it comes from. */
  unstaffed(roleId: string, month: string): { hours: number; deals: number } {
    const works = new Set<string>();
    let hours = 0;
    for (const n of this.needsByRole.get(`${roleId}|${month}`) ?? []) {
      if (!this.workByKey.has(n.key)) continue;
      const h = this.shownNeed(n).gap;
      if (h > 0.05) {
        hours += h;
        works.add(n.key);
      }
    }
    return { hours, deals: works.size };
  }

  /** A piece of work's roles in a month, each with its gap as shown. */
  gapsOf(key: string, month: string): { role: string; gap: number; unnamed: number }[] {
    return [...(this.needs.get(`${key}|${month}`)?.values() ?? [])]
      .map((n) => ({ role: n.role, gap: this.shownNeed(n).gap, unnamed: n.unnamed * this.weightOf(key) }))
      .filter((g) => g.gap > 0.05);
  }

  /** The people a group shows: by primary role, or none. */
  membersOf(roleId: string): ResourcePerson[] {
    return this.data.people.filter((p) => (p.primary_role_id ?? NO_ROLE) === roleId);
  }

  roleMonth(roleId: string, month: string): RoleMonth {
    const stored = this.rolePeriods.get(`${roleId}|${month}`);
    if (this.plain && roleId !== NO_ROLE) {
      const cap = stored?.capacity_hours ?? 0;
      const booked = stored?.booked_hours ?? 0;
      const unstaffed = this.unstaffed(roleId, month).hours;
      return { cap, booked, unstaffed, free: cap - booked - unstaffed };
    }
    let cap = 0;
    let booked = 0;
    for (const p of this.membersOf(roleId)) {
      const pm = this.personMonth(p.id, month);
      cap += pm.cap;
      booked += pm.booked;
    }
    if (roleId !== NO_ROLE && stored) cap = stored.capacity_hours;
    const unstaffed = roleId === NO_ROLE ? 0 : this.unstaffed(roleId, month).hours;
    return { cap, booked, unstaffed, free: cap - booked - unstaffed };
  }

  workMonth(key: string, month: string): WorkMonth {
    let demand = 0;
    let unstaffed = 0;
    let over = 0;
    for (const n of this.needs.get(`${key}|${month}`)?.values() ?? []) {
      const s = this.shownNeed(n);
      demand += s.demand;
      unstaffed += s.gap;
      over += s.over;
    }
    let resourced = 0;
    const people = new Set<string>();
    for (const pp of this.ppByWork.get(`${key}|${month}`) ?? []) {
      if (!pp.app_user_id) continue;
      const s = this.shownPP(pp);
      const h = s.confirmed + s.assigned;
      if (!h) continue;
      resourced += h;
      people.add(pp.app_user_id);
    }
    return { demand, resourced, unstaffed, over, people: people.size };
  }

  /** The hours billed for a person on a piece of work in a month (billable time entries). */
  billedOn(a: ResourceAssignment): number {
    return (this.ppByWork.get(`${this.keyOf(a)}|${a.month}`) ?? [])
      .filter((pp) => pp.app_user_id === a.app_user_id && pp.resource_role_id === a.resource_role_id)
      .reduce((sum, pp) => sum + pp.billed_hours, 0);
  }
}

// ── The grid ────────────────────────────────────────────────────────────────

export const PERSON_LEGEND: ResourceLegendItem[] = [
  { label: 'Mostly free', swatch: 'under-3' },
  { label: 'Some free', swatch: 'under-1' },
  { label: 'On target, 75–100%', swatch: 'target' },
  { label: 'Over', swatch: 'over-1' },
  { label: 'Well over', swatch: 'over-3' },
];
export const PROJECT_LEGEND: ResourceLegendItem[] = [
  { label: 'Fully staffed', swatch: 'target' },
  { label: 'Under 25% unstaffed', swatch: 'over-1' },
  { label: '25–50%', swatch: 'over-2' },
  { label: 'Half or more unstaffed', swatch: 'over-3' },
];
export const TIMELINE_LEGEND: ResourceLegendItem[] = [
  { label: 'Confirmed', swatch: 'confirmed' },
  { label: 'Assigned', swatch: 'assigned' },
  { label: 'Forecast, nobody named', swatch: 'forecast' },
];

/** Avatar palette slots, one per role in order, so a role's people share a colour. */
const TONES = [1, 5, 2, 4, 6, 3] as const;

export interface GridModel {
  months: ResourceMonth[];
  groups: ResourceGroup[];
  rowHeader: string;
  variant: 'grid' | 'bars';
  count: string;
  legend: ResourceLegendItem[];
  /** Keys of the groups with headers, for Collapse all. */
  headerKeys: string[];
}

/** The months in view, or just the selected one: what the summary and sorts count. */
export function countedMonths(months: string[], focus: string | null): string[] {
  return focus !== null && months.includes(focus) ? [focus] : months;
}

function personCell(pl: Planner, person: ResourcePerson, month: string): ResourceCell {
  const pm = pl.personMonth(person.id, month);
  const over = pm.cap > 0 && pm.u > 1.0001;
  const free = pm.cap - pm.booked;
  return {
    heat: utilizationHeat(pm.booked, pm.cap),
    lines: [
      line('Utilization', pm.cap ? pct(pm.u) : '–'),
      line('Resourced', hrs(pm.booked)),
      line('Capacity', hrs(pm.cap)),
      line('Available', `${over ? '−' : ''}${hrs(Math.abs(free))}`, over),
      line('Billed', monthIndex(month) <= monthIndex(pl.data.current_month) ? hrs(pm.billed) : '–'),
    ],
    label: `${person.name}, ${monthName(month, true)}: ${Math.round(pm.u * 100)}% utilized, ${fmt(pm.booked)} of ${fmt(pm.cap)} hours resourced${pm.note ? `, ${pm.note}` : ''}`,
  };
}

function sortPeople(pl: Planner, people: ResourcePerson[], kms: string[]): ResourcePerson[] {
  const key = pl.settings.personSort;
  const stat = new Map(
    people.map((p) => {
      let cap = 0;
      let booked = 0;
      let peak = 0;
      for (const m of kms) {
        const pm = pl.personMonth(p.id, m);
        cap += pm.cap;
        booked += pm.booked;
        if (pm.cap) peak = Math.max(peak, pm.u);
      }
      return [p.id, { avg: cap ? booked / cap : 0, peak, free: cap - booked }];
    }),
  );
  const s = (p: ResourcePerson) => stat.get(p.id)!;
  return [...people].sort((a, b) => {
    if (key === 'avg') return s(b).avg - s(a).avg || a.name.localeCompare(b.name);
    if (key === 'peak') return s(b).peak - s(a).peak || a.name.localeCompare(b.name);
    if (key === 'free') return s(b).free - s(a).free || a.name.localeCompare(b.name);
    return a.name.localeCompare(b.name);
  });
}

function personRow(pl: Planner, person: ResourcePerson, months: string[], grouped: boolean): ResourceRow {
  const toneIndex = pl.roles.findIndex((r) => r.id === person.primary_role_id);
  const others = person.role_ids.filter((r) => r !== person.primary_role_id).map((r) => pl.roleName(r));
  const sub = grouped
    ? others.length ? `Also ${others.join(', ')}` : undefined
    : `${person.primary_role_id ? pl.roleName(person.primary_role_id) : 'No role'}${others.length ? ` · also ${others.join(', ')}` : ''}`;
  const cells: Record<string, ResourceCell> = {};
  for (const m of months) cells[m] = personCell(pl, person, m);
  return {
    key: `p:${person.id}`,
    name: person.name,
    ...(sub ? { sub } : {}),
    href: personHref(person.id),
    avatar: { name: person.name, tone: toneIndex >= 0 ? TONES[toneIndex % TONES.length] : 3 },
    cells,
  };
}

/** The roles a planner groups people by: active roles, and any other with someone or something in it. */
function plannerRoles(pl: Planner): { id: string; name: string }[] {
  const used = (r: ResourceRole) =>
    pl.membersOf(r.id).length > 0 || pl.data.months.some((m) => pl.roleMonth(r.id, m).unstaffed > 0);
  const roles = pl.roles.filter((r) => r.active || used(r)).map((r) => ({ id: r.id, name: r.name }));
  if (pl.membersOf(NO_ROLE).length > 0) roles.push({ id: NO_ROLE, name: 'No role' });
  return roles;
}

function roleGroup(pl: Planner, role: { id: string; name: string }, months: string[], kms: string[]): ResourceGroup {
  const rows = sortPeople(pl, pl.membersOf(role.id), kms).map((p) => personRow(pl, p, months, true));
  if (pl.settings.show.forecast && role.id !== NO_ROLE) {
    const cells: Record<string, ResourceCell> = {};
    for (const m of months) {
      const u = pl.unstaffed(role.id, m);
      if (!u.hours) continue;
      cells[m] = {
        heat: 'over-1',
        demand: true,
        lines: [line('Unstaffed', hrs(u.hours)), line('Projects', String(u.deals))],
        label: `Not yet staffed, ${role.name}, ${monthName(m, true)}: ${fmt(u.hours)} hours`,
      };
    }
    if (Object.keys(cells).length) {
      rows.push({ key: `u:${role.id}`, name: 'Not yet staffed', sub: 'Demand nobody is named for', muted: true, inset: true, cells });
    }
  }
  const cells: Record<string, ResourceCell> = {};
  for (const m of months) {
    const rm = pl.roleMonth(role.id, m);
    const quiet = !rm.cap && !rm.booked && !rm.unstaffed;
    const heat: ResourceHeat = quiet ? 'none' : utilizationHeat(rm.booked + rm.unstaffed, rm.cap);
    cells[m] = {
      heat,
      lines: [
        line('Utilization', `${Math.round(rm.cap ? (rm.booked / rm.cap) * 100 : 0)}%`),
        line('Resourced', hrs(rm.booked)),
        line('Capacity', hrs(rm.cap)),
        line('Available', hrs(Math.max(0, rm.cap - rm.booked))),
        line('Unstaffed', hrs(rm.unstaffed), rm.free < 0),
      ],
      label: `${role.name}, ${monthName(m, true)}: ${fmt(Math.max(0, rm.cap - rm.booked))}h available, ${fmt(rm.unstaffed)}h of open demand${
        rm.free < 0 ? `, short ${fmt(-rm.free)}h` : `, ${fmt(rm.free)}h left after it`
      }`,
    };
  }
  return { key: `role:${role.id}`, name: role.name, sub: 'Includes open demand', cells, rows };
}

/** Work with demand or hours in any month in view. */
function workInView(pl: Planner, months: string[]): ResourceWork[] {
  return pl.data.work.filter((w) => !pl.isAliased(w) && months.some((m) => {
    const wm = pl.workMonth(workKey(w), m);
    return wm.demand > 0 || wm.resourced > 0;
  }));
}

function sortWork(pl: Planner, list: ResourceWork[], kms: string[]): ResourceWork[] {
  const key = pl.settings.projectSort;
  const stat = new Map(
    list.map((w) => {
      let total = 0;
      let unnamed = 0;
      for (const m of kms) {
        const wm = pl.workMonth(workKey(w), m);
        total += wm.demand;
        unnamed += wm.unstaffed;
      }
      return [workKey(w), { total, unnamed }];
    }),
  );
  const s = (w: ResourceWork) => stat.get(workKey(w))!;
  const byAccount = (a: ResourceWork, b: ResourceWork) => a.account_name.localeCompare(b.account_name) || a.name.localeCompare(b.name);
  return [...list].sort((a, b) => {
    if (key === 'hours') return s(b).total - s(a).total || byAccount(a, b);
    if (key === 'unstaffed') return s(b).unnamed - s(a).unnamed || byAccount(a, b);
    if (key === 'start') return (a.first_month ?? '9999').localeCompare(b.first_month ?? '9999') || byAccount(a, b);
    return byAccount(a, b);
  });
}

/** A project-month's figures, one line each, the gap and any excess in the warm ink. */
export function workLines(wm: WorkMonth): ResourceLine[] {
  const staffed = wm.over > 0 ? `${pct(wm.resourced / Math.max(wm.demand - wm.over, 1))}, over ${fmt(wm.over)}h`
    : wm.demand ? (wm.resourced ? pct(Math.min(1, wm.resourced / wm.demand)) : 'Nobody yet') : '–';
  return [
    line('Demand', hrs(wm.demand)),
    line('Resourced', hrs(wm.resourced)),
    line('Unstaffed', hrs(wm.unstaffed), wm.unstaffed > 0.05),
    line('Staffed', staffed, wm.over > 0 || (wm.demand > 0 && !wm.resourced)),
    line('People', String(wm.people)),
  ];
}

function workRow(pl: Planner, w: ResourceWork, months: string[]): ResourceRow {
  const cells: Record<string, ResourceCell> = {};
  for (const m of months) {
    const wm = pl.workMonth(workKey(w), m);
    if (!wm.demand && !wm.resourced) continue;
    const gap = wm.demand ? wm.unstaffed / wm.demand : 0;
    cells[m] = {
      heat: gap === 0 ? 'target' : gap < 0.25 ? 'over-1' : gap < 0.5 ? 'over-2' : 'over-3',
      lines: workLines(wm),
      label: `${w.account_name}, ${monthName(m, true)}: ${fmt(wm.demand)} hours needed, ${fmt(wm.resourced)} resourced, ${fmt(wm.unstaffed)} not yet staffed${
        wm.over > 0 ? `, ${fmt(wm.over)} over what was sold` : ''
      }`,
    };
  }
  return { key: `w:${workKey(w)}`, name: w.account_name, sub: workSub(w), href: workHref(w), cells };
}

function timelineRow(pl: Planner, w: ResourceWork, months: string[]): ResourceRow {
  const key = workKey(w);
  const deal = isDeal(w);
  const cells: Record<string, ResourceCell> = {};
  for (const m of months) {
    const h = pl.workMonth(key, m).demand;
    if (h) cells[m] = { bar: { value: hrs(h), status: deal ? 'assigned' : 'confirmed' }, label: `${w.account_name}, ${monthName(m, true)}: ${fmt(h)} hours` };
  }
  // One child per person and role on the work, then one per role for demand nobody is named for.
  const lines = new Map<string, { user: string | null; role: string | null; cells: Record<string, number> }>();
  const add = (user: string | null, role: string | null, month: string, h: number) => {
    const id = `${user ?? '-'}|${role ?? '-'}`;
    const entry = lines.get(id) ?? { user, role, cells: {} };
    entry.cells[month] = (entry.cells[month] ?? 0) + h;
    lines.set(id, entry);
  };
  for (const pp of pl.data.project_periods) {
    if (!pp.app_user_id || pl.keyOf(pp) !== key || !months.includes(pp.month)) continue;
    const s = pl.shownPP(pp);
    const h = s.confirmed + s.assigned;
    if (h) add(pp.app_user_id, pp.resource_role_id, pp.month, h);
  }
  for (const m of months) for (const g of pl.gapsOf(key, m)) add(null, g.role === NO_ROLE ? null : g.role, m, g.gap);
  const children: ResourceRow[] = [...lines.entries()]
    .sort(([, a], [, b]) => (a.user === null ? 1 : 0) - (b.user === null ? 1 : 0) || pl.roleName(a.role).localeCompare(pl.roleName(b.role)))
    .map(([id, entry]) => {
      const who = entry.user ? pl.personById.get(entry.user)?.name ?? 'Someone' : 'Unnamed';
      const status: AssignmentStatus = !deal ? 'confirmed' : entry.user === null ? 'forecast' : 'assigned';
      const child: Record<string, ResourceCell> = {};
      for (const [m, h] of Object.entries(entry.cells)) {
        child[m] = { bar: { value: hrs(h), status }, label: `${who}, ${monthName(m, true)}: ${fmt(h)} hours, ${status}` };
      }
      return {
        key: `w:${key}|${id}`,
        name: who,
        muted: entry.user === null,
        ...(entry.user ? { href: personHref(entry.user) } : {}),
        sub: pl.roleName(entry.role),
        cells: child,
      };
    });
  return { key: `w:${key}`, name: w.account_name, sub: workSub(w), href: workHref(w), cells, children };
}

function workGroups(pl: Planner, months: string[], kms: string[], make: (w: ResourceWork) => ResourceRow): ResourceGroup[] {
  const list = sortWork(pl, workInView(pl, months), kms);
  if (pl.settings.projectGroup === 'none') return [{ key: 'all', rows: list.map(make) }];
  return ([
    ['active', 'Active projects', false, 'Won and in delivery'],
    ['pipeline', 'Pipeline', true, 'Open deals on their primary quote'],
  ] as const).map(([key, name, deal, sub]) => {
    const ids = list.filter((w) => isDeal(w) === deal);
    const cells: Record<string, ResourceCell> = {};
    for (const m of months) {
      let t = 0;
      let u = 0;
      let d = 0;
      for (const w of ids) {
        const wm = pl.workMonth(workKey(w), m);
        d += wm.demand;
        t += wm.resourced;
        u += wm.unstaffed;
      }
      cells[m] = {
        heat: 'none',
        selectable: false,
        lines: [line('Demand', hrs(d)), line('Resourced', hrs(t)), line('Unstaffed', hrs(u), u > 0.05)],
        label: `${name}, ${monthName(m, true)}`,
      };
    }
    return { key: `proj:${key}`, name, sub, cells, rows: ids.map(make) };
  });
}

/** The grid for the view: month headings with their totals, groups and rows, the footer. */
export function buildGrid(pl: Planner): GridModel {
  const { view, focus } = pl.settings;
  const months = pl.data.months;
  const kms = countedMonths(months, focus);
  const people = pl.data.people;
  const current = monthIndex(pl.data.current_month);

  const monthCols: ResourceMonth[] = months.map((m) => {
    let totals: ResourceLine[];
    if (view === 'people') {
      let b = 0;
      let c = 0;
      for (const p of people) {
        const pm = pl.personMonth(p.id, m);
        b += pm.booked;
        c += pm.cap;
      }
      totals = [line('Utilization', `${Math.round(c ? (b / c) * 100 : 0)}%`), line('Resourced', hrs(b)), line('Capacity', hrs(c))];
    } else {
      let dem = 0;
      let res = 0;
      let un = 0;
      let c = 0;
      for (const w of pl.data.work) {
        if (pl.isAliased(w)) continue;
        const wm = pl.workMonth(workKey(w), m);
        dem += wm.demand;
        res += wm.resourced;
        un += wm.unstaffed;
      }
      for (const p of people) c += pl.personMonth(p.id, m).cap;
      totals = view === 'projects'
        ? [line('Demand', hrs(dem)), line('Resourced', hrs(res)), line('Unstaffed', hrs(un), un > 0.05), line('Capacity', hrs(c))]
        : [line('Demand', hrs(dem)), line('Capacity', hrs(c))];
    }
    return { key: m, label: shortMonth(m), year: yearOf(m), current: monthIndex(m) === current, totals };
  });

  let groups: ResourceGroup[];
  if (view === 'people') {
    groups = pl.settings.personGroup === 'role'
      ? plannerRoles(pl).map((r) => roleGroup(pl, r, months, kms))
      : [{ key: 'all', rows: sortPeople(pl, people, kms).map((p) => personRow(pl, p, months, false)) }];
  } else if (view === 'projects') {
    groups = workGroups(pl, months, kms, (w) => workRow(pl, w, months));
  } else {
    groups = workGroups(pl, months, kms, (w) => timelineRow(pl, w, months));
  }
  const works = workInView(pl, months).length;
  return {
    months: monthCols,
    groups,
    rowHeader: view === 'people' ? (pl.settings.personGroup === 'role' ? 'Role / person' : 'Person') : view === 'projects' ? 'Project' : 'Project / person',
    variant: view === 'timeline' ? 'bars' : 'grid',
    count: view === 'people'
      ? `Showing ${people.length} of ${people.length} ${people.length === 1 ? 'person' : 'people'}`
      : `Showing ${works} ${works === 1 ? 'project or deal' : 'projects and deals'}`,
    legend: view === 'people' ? PERSON_LEGEND : view === 'projects' ? PROJECT_LEGEND : TIMELINE_LEGEND,
    headerKeys: groups.filter((g) => g.name).map((g) => g.key),
  };
}

// ── The summary ─────────────────────────────────────────────────────────────

/**
 * The header's five figures over the months counted: a monthly average
 * ("h/mo") over several months, the month itself ("h") for one. When every
 * month counted is past, Billed takes Available's place.
 */
export function buildMetrics(pl: Planner): MetricRowItem[] {
  const kms = countedMonths(pl.data.months, pl.settings.focus);
  let cap = 0;
  let booked = 0;
  let billed = 0;
  let unstaffed = 0;
  for (const p of pl.data.people) {
    for (const m of kms) {
      const pm = pl.personMonth(p.id, m);
      cap += pm.cap;
      booked += pm.booked;
      billed += pm.billed;
    }
  }
  for (const m of kms) for (const r of pl.roles) unstaffed += pl.roleMonth(r.id, m).unstaffed;
  const n = Math.max(kms.length, 1);
  const unit = n > 1 ? 'h/mo' : 'h';
  const per = (h: number) => ({ value: fmt(h / n), unit });
  const allPast = kms.length > 0 && kms.every((m) => monthIndex(m) < monthIndex(pl.data.current_month));
  return [
    { key: 'cap', label: 'Capacity', ...per(cap), sub: 'Hours available to work' },
    { key: 'booked', label: 'Resourced', ...per(booked), sub: 'Confirmed + assigned hours' },
    {
      key: 'unstaffed', label: 'Unstaffed', ...per(unstaffed),
      sub: pl.settings.weighted ? 'Nobody named, deals weighted' : 'Demand nobody is named for', tone: unstaffed > 0.05 ? 'negative' : undefined,
    },
    allPast
      ? { key: 'billed', label: 'Billed', ...per(billed), sub: 'Billable time entries' }
      : { key: 'avail', label: 'Available', ...per(cap - booked), sub: 'Capacity − resourced' },
    { key: 'util', label: 'Utilization', value: `${Math.round(cap ? (booked / cap) * 100 : 0)}%`, sub: 'Resourced ÷ capacity' },
  ];
}

/** "Oct 2026 – Mar 2027", or the selected month in full. */
export function focusLabel(months: string[], focus: string | null): string {
  if (focus !== null && months.includes(focus)) return monthName(focus, true);
  if (!months.length) return '';
  return `${monthName(months[0]!)}${months.length > 1 ? ` – ${monthName(months[months.length - 1]!)}` : ''}`;
}

/** Whether there is anything to plan: people in delivery, or hours booked on anything. */
export function hasAnything(data: ResourcingData): boolean {
  return data.people.length > 0 || data.assignments.length > 0 || data.project_periods.length > 0;
}

// ── Selection and the detail panel ─────────────────────────────────────────

export type Selection = { type: 'person' | 'role' | 'unstaffed' | 'project'; key: string; month: string };

/** A grid cell's address as the selection it opens. */
export function selectionOf(ref: ResourceCellRef): Selection | null {
  const month = ref.monthKey;
  if (ref.kind === 'group') {
    return ref.rowKey.startsWith('role:') ? { type: 'role', key: ref.rowKey.slice(5), month } : null;
  }
  if (ref.rowKey.startsWith('u:')) return { type: 'unstaffed', key: ref.rowKey.slice(2), month };
  if (ref.rowKey.startsWith('p:')) return { type: 'person', key: ref.rowKey.slice(2), month };
  if (ref.rowKey.startsWith('w:')) return { type: 'project', key: ref.rowKey.slice(2).split('|')[0]!, month };
  return null;
}

export function cellOf(sel: Selection | null): ResourceCellRef | null {
  if (!sel) return null;
  if (sel.type === 'role') return { kind: 'group', rowKey: `role:${sel.key}`, monthKey: sel.month };
  if (sel.type === 'unstaffed') return { kind: 'row', rowKey: `u:${sel.key}`, monthKey: sel.month };
  if (sel.type === 'person') return { kind: 'row', rowKey: `p:${sel.key}`, monthKey: sel.month };
  return { kind: 'row', rowKey: `w:${sel.key}`, monthKey: sel.month };
}

export const sameSelection = (a: Selection | null, b: Selection | null): boolean =>
  !!a && !!b && a.type === b.type && a.key === b.key && a.month === b.month;

export interface PanelItem {
  key: string;
  title: string;
  href?: string;
  sub: string;
  value: string;
  valueSub: string;
  /** An assignment this row can move: Reassign or Assign. */
  move?: { assignment: ResourceAssignment; verb: 'Reassign' | 'Assign' };
  /** An assignment whose hours can be changed in place, as stored (not weighted). */
  edit?: { id: string; hours: number };
}
export interface PanelSection {
  title: string;
  empty: string;
  items: PanelItem[];
}
export interface PanelModel {
  eyebrow: string;
  title: string;
  sub: string;
  stats: { label: string; value: string; over?: boolean }[];
  note: string | null;
  sections: PanelSection[];
  primary: { label: string; action: 'assign-person' | 'name-someone' | 'open' ; href?: string };
  secondary: { label: string; href: string } | null;
  /** The deal whose sold and actual role split the panel shows, for work that has one. */
  splitFor?: string;
}

/** Demand on a piece of work with no assignment row behind it: sold hours nobody holds yet. */
function unassignedItem(w: ResourceWork, roleName: string, hours: number): PanelItem {
  return {
    key: `gap:${workKey(w)}:${roleName}`,
    title: w.account_name,
    href: workHref(w),
    sub: `${w.name} · ${roleName} · Not assigned`,
    value: hrs(hours),
    valueSub: 'sold, nobody named',
  };
}

function assignmentItem(pl: Planner, a: ResourceAssignment, verb: 'Reassign' | 'Assign' | null): PanelItem {
  const work = pl.workByKey.get(pl.keyOf(a));
  const h = pl.shown(a);
  const billed = pl.billedOn(a);
  const likely = a.status !== 'confirmed' && work ? `, ${Math.round(work.probability)}% likely` : '';
  return {
    key: a.id,
    title: work?.account_name ?? 'Unknown work',
    ...(work ? { href: workHref(work) } : {}),
    sub: verb === 'Assign'
      ? `${work?.name ?? ''} · ${Math.round(work?.probability ?? 0)}% likely`
      : `${work?.name ?? ''} · ${pl.roleName(a.resource_role_id)} · ${STATUS_LABEL[a.status]}${likely}`,
    value: hrs(h),
    valueSub: verb === 'Assign' ? 'nobody named' : billed > 0 ? `billed ${fmt(billed)}h` : '',
    ...(verb ? { move: { assignment: a, verb } } : {}),
  };
}

/** What the detail panel shows for a selected cell, or null when it no longer exists. */
export function buildPanel(pl: Planner, sel: Selection): PanelModel | null {
  const m = sel.month;
  const ml = monthName(m, true);
  const past = monthIndex(m) <= monthIndex(pl.data.current_month);
  const inMonth = pl.data.assignments.filter((a) => a.month === m && pl.shown(a) > 0);

  if (sel.type === 'person') {
    const person = pl.personById.get(sel.key);
    if (!person) return null;
    const pm = pl.personMonth(person.id, m);
    const free = pm.cap - pm.booked;
    const mine = inMonth.filter((a) => a.app_user_id === person.id);
    // The title names the work; a second line only tells apart rows on the same work.
    const same = (a: ResourceAssignment) => mine.filter((b) => pl.keyOf(b) === pl.keyOf(a));
    const items = mine.map((a): PanelItem => {
      const twins = same(a);
      const statusDiffers = twins.some((b) => b.status !== a.status);
      const roleDiffers = twins.some((b) => b.resource_role_id !== a.resource_role_id);
      const sub = twins.length < 2 ? ''
        : [roleDiffers || !statusDiffers ? pl.roleName(a.resource_role_id) : '', statusDiffers ? STATUS_LABEL[a.status] : ''].filter(Boolean).join(' · ');
      return { ...assignmentItem(pl, a, 'Reassign'), sub, edit: { id: a.id, hours: a.hours } };
    });
    return {
      eyebrow: ml,
      title: person.name,
      sub: person.role_ids.length ? person.role_ids.map((r) => pl.roleName(r)).join(' · ') : 'No role yet',
      stats: [
        { label: 'Capacity', value: hrs(pm.cap) },
        { label: 'Resourced', value: hrs(pm.booked) },
        { label: free < 0 ? 'Over by' : 'Available', value: hrs(Math.abs(free)), over: free < 0 },
      ],
      note: free < 0
        ? `${Math.round(pm.u * 100)}% of capacity resourced in ${ml}.${pm.note ? ` ${pm.note} this month.` : ''}`
        : pm.note ? `${pm.note}: capacity reduced this month.` : null,
      sections: [{ title: past ? 'Assignments and billed time' : 'Assignments', empty: 'Nothing assigned this month.', items }],
      primary: { label: 'Assign to a project', action: 'assign-person' },
      secondary: { label: 'Edit capacity', href: personHref(person.id) },
    };
  }

  if (sel.type === 'role' || sel.type === 'unstaffed') {
    const rm = pl.roleMonth(sel.key, m);
    const needs = inMonth
      .filter((a) => a.app_user_id === null && a.resource_role_id === sel.key)
      .map((a) => assignmentItem(pl, a, 'Assign'));
    for (const w of pl.data.work) {
      const g = pl.gapsOf(workKey(w), m).find((x) => x.role === sel.key);
      const extra = g ? g.gap - g.unnamed : 0;
      if (extra > 0.05) needs.push(unassignedItem(w, pl.roleName(sel.key), extra));
    }
    const available = pl.data.people
      .filter((p) => p.role_ids.includes(sel.key) || (sel.key === NO_ROLE && !p.primary_role_id))
      .map((p) => ({ p, pm: pl.personMonth(p.id, m) }))
      .filter(({ pm }) => pm.cap - pm.booked > 0)
      .sort((a, b) => b.pm.cap - b.pm.booked - (a.pm.cap - a.pm.booked))
      .slice(0, 4)
      .map(({ p, pm }): PanelItem => ({
        key: p.id, title: p.name, href: personHref(p.id), sub: `${Math.round(pm.u * 100)}% resourced`, value: hrs(pm.cap - pm.booked), valueSub: 'available',
      }));
    return {
      eyebrow: ml,
      title: pl.roleName(sel.key === NO_ROLE ? null : sel.key),
      sub: sel.type === 'unstaffed' ? 'Demand nobody is named for yet' : 'Capacity and demand for the role',
      stats: [
        { label: 'Capacity', value: hrs(rm.cap) },
        { label: 'Resourced', value: hrs(rm.booked) },
        { label: 'Unstaffed', value: hrs(rm.unstaffed) },
      ],
      note: rm.free < 0 ? `Short ${fmt(-rm.free)}h in ${ml} once all demand is staffed.` : null,
      sections: [
        { title: 'Needs staffing', empty: 'No unnamed demand this month.', items: needs },
        { title: 'Most available in this role', empty: 'Nobody in this role has hours available.', items: available },
      ],
      primary: { label: 'Name someone', action: 'name-someone' },
      secondary: sel.key === NO_ROLE ? null : { label: 'Open role report', href: `/resource-roles/${encodeURIComponent(sel.key)}` },
    };
  }

  const work = pl.workByKey.get(sel.key);
  if (!work) return null;
  const wm = pl.workMonth(sel.key, m);
  const deal = isDeal(work);
  const rows = inMonth
    .filter((a) => pl.keyOf(a) === sel.key)
    .map((a): PanelItem => {
      const billed = pl.billedOn(a);
      const who = a.app_user_id ? pl.personById.get(a.app_user_id)?.name ?? 'Someone' : 'Unnamed';
      return {
        key: a.id,
        title: who,
        ...(a.app_user_id ? { href: personHref(a.app_user_id) } : {}),
        sub: `${pl.roleName(a.resource_role_id)} · ${STATUS_LABEL[a.status]}`,
        value: hrs(pl.shown(a)),
        valueSub: billed > 0 ? `billed ${fmt(billed)}h` : '',
      };
    });
  for (const g of pl.gapsOf(sel.key, m)) {
    const extra = g.gap - g.unnamed;
    if (extra > 0.05) rows.push({ ...unassignedItem(work, pl.roleName(g.role), extra), title: 'Nobody assigned' });
  }
  const runs = work.first_month && work.last_month ? `${shortMonth(work.first_month)}–${shortMonth(work.last_month)}` : '–';
  return {
    eyebrow: ml,
    title: work.account_name,
    sub: `${work.name} · ${workType(work)}`,
    stats: [
      { label: 'Demand', value: hrs(wm.demand) },
      { label: 'Resourced', value: hrs(wm.resourced) },
      { label: wm.over > 0 ? 'Over by' : 'Unstaffed', value: hrs(wm.over > 0 ? wm.over : wm.unstaffed), over: wm.over > 0 || wm.unstaffed > 0.05 },
    ],
    note: wm.demand > 0 && !wm.resourced
      ? `Nobody is assigned in ${ml}: all ${fmt(wm.demand)}h are unstaffed.`
      : `${deal ? `${Math.round(work.probability)}% likely` : `Runs ${runs}`}.`,
    sections: [{ title: 'Staffing this month', empty: 'No hours this month.', items: rows }],
    primary: { label: work.kind === 'project' ? 'Open project' : 'Open opportunity', action: 'open', href: workHref(work) },
    secondary: { label: 'Edit assignments', href: `${workHref(work)}/related/resource-assignments` },
    ...(work.opportunity_id ? { splitFor: work.opportunity_id } : {}),
  };
}

// ── Editing a month's hours ─────────────────────────────────────────────────

/**
 * The payload with one assignment's hours changed, and the stored roll-ups it
 * feeds moved by the same amount, so the page shows the edit before the
 * server's roll-ups come back.
 */
export function withHours(data: ResourcingData, id: string, hours: number): ResourcingData {
  const a = data.assignments.find((x) => x.id === id);
  if (!a || a.hours === hours) return data;
  const delta = hours - a.hours;
  const field = a.status === 'confirmed' ? 'confirmed_hours' : a.status === 'assigned' ? 'assigned_hours' : 'forecast_hours';
  const primary = a.app_user_id ? data.people.find((p) => p.id === a.app_user_id)?.primary_role_id ?? null : null;
  return {
    ...data,
    assignments: data.assignments.map((x) => (x.id === id ? { ...x, hours } : x)),
    periods: a.app_user_id === null ? data.periods : data.periods.map((p) => (p.app_user_id === a.app_user_id && p.month === a.month
      ? {
        ...p,
        booked_hours: p.booked_hours + delta,
        ...(field === 'forecast_hours' ? {} : { [field]: p[field] + delta }),
      }
      : p)),
    project_periods: data.project_periods.map((pp) => (pp.app_user_id === a.app_user_id && pp.resource_role_id === a.resource_role_id
      && pp.month === a.month && pp.project_id === a.project_id && (a.project_id !== null || pp.opportunity_id === a.opportunity_id)
      ? { ...pp, [field]: pp[field] + delta }
      : pp)),
    role_periods: primary === null ? data.role_periods : data.role_periods.map((r) => (r.resource_role_id === primary && r.month === a.month
      ? { ...r, booked_hours: r.booked_hours + delta }
      : r)),
  };
}

export function hoursText(pl: Planner, id: string, hours: number, previous: number): string {
  const a = pl.data.assignments.find((x) => x.id === id);
  const work = a ? pl.workByKey.get(pl.keyOf(a)) : undefined;
  const who = a?.app_user_id ? pl.personById.get(a.app_user_id)?.name ?? 'Someone' : 'Nobody named';
  return `${who} on ${work?.account_name ?? 'the work'}, ${a ? monthName(a.month, true) : ''}: ${hrs(previous)} → ${hrs(hours)}.`;
}

// ── Drag to reassign ────────────────────────────────────────────────────────

export interface Drag {
  assignment: ResourceAssignment;
  /** The hours as shown, which the projections add. */
  hours: number;
}

/** Everyone the month could move to: people who fill the role and have capacity that month, with where they would land. */
export function moveTargets(pl: Planner, drag: Drag): ResourceMove {
  const { assignment: a, hours } = drag;
  const targets: ResourceMove['targets'] = {};
  for (const p of pl.data.people) {
    if (p.id === a.app_user_id || !p.role_ids.includes(a.resource_role_id)) continue;
    const pm = pl.personMonth(p.id, a.month);
    if (!pm.cap) continue;
    const after = (pm.booked + hours) / pm.cap;
    const overBy = pm.booked + hours - pm.cap;
    targets[`p:${p.id}`] = {
      [a.month]: {
        lines: [
          line('Utilization', pct(after), after > 1),
          line('Resourced', hrs(pm.booked + hours)),
          line('Capacity', hrs(pm.cap)),
          line('Available', `${overBy > 0 ? '−' : ''}${hrs(Math.abs(overBy))}`, overBy > 0),
          line('Billed', monthIndex(a.month) <= monthIndex(pl.data.current_month) ? hrs(pm.billed) : '–'),
        ],
        label: `Move here: ${p.name} would be ${Math.round(after * 100)}% utilized`,
      },
    };
  }
  return { targets };
}

export function movingText(pl: Planner, drag: Drag): string {
  const work = pl.workByKey.get(pl.keyOf(drag.assignment));
  return `Moving ${work?.account_name ?? 'this work'}, ${hrs(drag.hours)} in ${monthName(drag.assignment.month, true)}. Drop it on anyone outlined in that month, or click their cell. Each one shows where they would land.`;
}

export function movedText(pl: Planner, drag: Drag, toId: string): string {
  const a = drag.assignment;
  const work = pl.workByKey.get(pl.keyOf(a));
  const to = pl.personById.get(toId)?.name ?? 'them';
  const from = a.app_user_id ? pl.personById.get(a.app_user_id)?.name ?? 'someone' : null;
  return `${work?.account_name ?? 'The work'}, ${hrs(drag.hours)} in ${monthName(a.month, true)}: ${from === null ? 'assigned to ' : `moved from ${from} to `}${to}. Other months are unchanged.`;
}

// ── New assignment ──────────────────────────────────────────────────────────

export interface NewForm {
  work: string;
  role: string;
  /** '' for nobody named yet. */
  person: string;
  from: string;
  to: string;
  /** The hours each month repeats. */
  hours: string;
  /** Months whose hours were edited away from `hours`, by YYYY-MM. */
  overrides: Record<string, string>;
}

/** One month in the dialog's table: its hours and what they do. */
export interface NewFormRow {
  month: string;
  label: string;
  hours: string;
  edited: boolean;
  role: { text: string; warn: boolean };
  overall: { text: string; warn: boolean };
}

export interface NewFormModel {
  status: AssignmentStatus | null;
  statusNote: string;
  error: string;
  months: string[];
  rows: NewFormRow[];
  /** Says what the two effect columns measure. */
  effectNote: string;
  /** Runs of months with the same hours: one create each. Months with no hours are left out. */
  runs: { from: string; to: string; hours: number }[];
  createText: string;
  eligible: ResourcePerson[];
}

const STATUS_NOTES: Record<AssignmentStatus, string> = {
  confirmed: 'Confirmed: the project is won.',
  assigned: 'Assigned: a person is named on an open opportunity.',
  forecast: 'Forecast: nobody named yet. It appears under Not yet staffed.',
};

export const MAX_MONTH_HOURS = 744;
const hoursOf = (text: string): number => Number(text);
const share = (part: number, whole: number): number => (whole ? part / whole : part ? 9 : 0);
const pctText = (u: number): string => (u >= 9 ? '–' : `${Math.round(u * 100)}%`);

/**
 * The New assignment dialog's checks and its effect, month by month: on the
 * role (its resourced and unstaffed hours against its capacity) and overall
 * (the person's utilization, or with nobody named, the whole team's).
 * `pl` holds the dialog's own months.
 */
export function newFormModel(pl: Planner, form: NewForm): NewFormModel {
  const work = pl.workByKey.get(form.work);
  const eligible = pl.data.people.filter((p) => p.role_ids.includes(form.role));
  const person = form.person === '' ? null : pl.personById.get(form.person) ?? null;
  const months = monthIndex(form.to) >= monthIndex(form.from) ? monthRange(form.from, form.to) : [];
  const won = !!work && !isDeal(work);
  const status: AssignmentStatus | null = !work ? null : won ? 'confirmed' : person === null ? 'forecast' : 'assigned';
  const shown = months.slice(0, 36);
  const hoursText = (m: string): string => form.overrides[m] ?? form.hours;
  const bad = shown.find((m) => {
    const h = hoursOf(hoursText(m));
    return hoursText(m).trim() === '' || !Number.isFinite(h) || h < 0 || h > MAX_MONTH_HOURS;
  });
  const total = shown.reduce((sum, m) => sum + (bad ? 0 : hoursOf(hoursText(m))), 0);
  const error = !work
    ? 'Choose a project or opportunity.'
    : !form.role ? 'Choose a role.'
      : !months.length ? 'The end month is before the start month.'
        : months.length > 36 ? 'Add at most 36 months at a time.'
          : bad ? `Enter 0 to ${MAX_MONTH_HOURS} hours for ${monthName(bad, true)}.`
            : !total ? 'Enter the hours per month.'
              : won && person === null ? 'Won projects need a named person.' : '';

  const teamMonth = (m: string) => {
    let cap = 0;
    let used = 0;
    for (const p of pl.data.people) {
      const pm = pl.personMonth(p.id, m);
      cap += pm.cap;
      used += pm.booked;
    }
    for (const r of pl.roles) used += pl.roleMonth(r.id, m).unstaffed;
    return { cap, used };
  };
  const rows = shown.map((m): NewFormRow => {
    const text = hoursText(m);
    const h = Math.max(0, hoursOf(text) || 0);
    const rm = pl.roleMonth(form.role, m);
    const roleBefore = share(rm.booked + rm.unstaffed, rm.cap);
    const roleAfter = share(rm.booked + rm.unstaffed + h, rm.cap);
    let overall: NewFormRow['overall'];
    if (person) {
      const pm = pl.personMonth(person.id, m);
      const after = share(pm.booked + h, pm.cap);
      const over = pm.booked + h - pm.cap;
      overall = pm.cap
        ? { text: `${pctText(pm.u)} → ${pctText(after)}${over > 0 ? `, over ${fmt(over)}h` : ''}`, warn: over > 0 }
        : { text: 'No capacity', warn: h > 0 };
    } else {
      const t = teamMonth(m);
      const after = share(t.used + h, t.cap);
      overall = t.cap ? { text: `${pctText(share(t.used, t.cap))} → ${pctText(after)}`, warn: after > 1 } : { text: 'No capacity', warn: h > 0 };
    }
    return {
      month: m,
      label: monthName(m),
      hours: text,
      edited: m in form.overrides && form.overrides[m] !== form.hours,
      role: rm.cap ? { text: `${pctText(roleBefore)} → ${pctText(roleAfter)}`, warn: roleAfter > 1 } : { text: 'No capacity', warn: h > 0 },
      overall,
    };
  });

  const runs: NewFormModel['runs'] = [];
  if (!error) {
    for (const m of shown) {
      const h = hoursOf(hoursText(m));
      const last = runs[runs.length - 1];
      if (h > 0 && last && last.hours === h && addMonths(last.to, 1) === m) last.to = m;
      else if (h > 0) runs.push({ from: m, to: m, hours: h });
    }
  }
  const filled = shown.filter((m) => hoursOf(hoursText(m)) > 0).length;
  return {
    status,
    statusNote: status ? `Saves as ${STATUS_NOTES[status]}` : '',
    error,
    months,
    rows,
    effectNote: `Role effect: ${pl.roleName(form.role)}, resourced and unstaffed hours ÷ capacity. Overall effect: ${
      person ? `${person.name}'s utilization` : 'the whole team, the same way'
    }.`,
    runs,
    createText: `Add ${filled || months.length} ${(filled || months.length) === 1 ? 'month' : 'months'}`,
    eligible,
  };
}

export function addedText(pl: Planner, form: NewForm, runs: NewFormModel['runs']): string {
  const work = pl.workByKey.get(form.work);
  const person = form.person ? pl.personById.get(form.person) : undefined;
  const who = person ? person.name : `unstaffed ${pl.roleName(form.role)}`;
  const first = runs[0]?.from ?? form.from;
  const last = runs[runs.length - 1]?.to ?? form.to;
  const span = first === last ? monthName(first) : `${monthName(first)} – ${monthName(last)}`;
  const uniform = runs.length > 0 && runs.every((r) => r.hours === runs[0]!.hours);
  const total = runs.reduce((sum, r) => sum + r.hours * (monthIndex(r.to) - monthIndex(r.from) + 1), 0);
  const amount = uniform ? `${fmt(runs[0]!.hours)}h a month` : `${fmt(total)}h`;
  return `Added ${amount} for ${who} on ${work?.account_name ?? 'the work'}, ${span}.`;
}

/** The New assignment dialog's choice of work: open opportunities first by account, then won work. */
export function workOptions(data: ResourcingData): { value: string; label: string; hint: string }[] {
  return [...data.work]
    .sort((a, b) => Number(isDeal(b)) - Number(isDeal(a)) || a.account_name.localeCompare(b.account_name) || a.name.localeCompare(b.name))
    .map((w) => ({
      value: workKey(w),
      label: `${w.account_name} · ${w.name}`,
      hint: isDeal(w) ? `Opportunity · ${Math.round(w.probability)}%` : 'Project',
    }));
}
